import {
    defineElement,
    FC,
    onDidLoad,
    useBindMethod,
    useElement,
    useEvent,
    useListen,
    useProp,
} from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { debounce, Procedure, useStore } from '@bpd-library/utilities';
import { virtualView2dPointerEvents } from '@features/virtual-tour/constants';
import { VirtualTourState, virtualTourStore } from '@features/virtual-tour/store';
import {
    ScreenCoordinates,
    VirtualTourImageView,
    VRTourImageInterface,
    VRTourPointerFilter,
} from '@features/virtual-tour/types';
import {
    useMousePosition,
    usePanningImage,
    useResizableViewport,
} from '@features/virtual-tour/utilities/hooks';
import { useImagePointers } from '@features/virtual-tour/utilities/hooks/use-image-pointers';
import { useStylesheet } from '@utilities/hooks';
import { deviceWidth, useMedia } from '@utilities/media-query';

import style from './vr-tour-image-view.scss';

const VIEWPORT_MOUSE_AWAY_CLASS = 'vr-tour-image-view__viewport--mouse-away';

const VRTourImageView: FC<VRTourImageInterface> = () => {
    useStylesheet(style);
    const [onPointerClick] = useProp<Procedure>('onPointerClick');
    const [vrTourId] = useProp<string>('vrTourId', '');
    const [pointerHighlighted] = useProp<VRTourPointerFilter | null>('pointerHighlighted', null);

    const [vrStore, subscribe] = useStore<VirtualTourState>(virtualTourStore);

    const viewport = useElement<HTMLElement>('[js-hook-vr-tour-image-view-viewport]');
    const imageContainer = useElement<HTMLElement>('[js-hook-vr-tour-image-view-image-container]');

    const pointerHover = useEvent({ eventName: virtualView2dPointerEvents.LABEL_HOVER });
    const onPointerHover = (id: string, active: boolean) => {
        pointerHover.emit({ id, active });
    };

    const panningImage = usePanningImage({
        imageContainer,
        viewport,
    });

    const imagePointers = useImagePointers({
        imageContainer,
        onPointerClick,
        onPointerHover,
    });

    const {
        dimensions: viewportDimensions,
        position: viewportPosition,
        refresh,
    } = useResizableViewport({
        viewport,
        onResize: panningImage.resize,
    });

    const { updatePosition } = useMousePosition({
        viewport,
        onUpdate: panningImage.pan,
    });

    const onDimensionsChanged = debounce(refresh, 100);

    useListen(window, 'resize', onDimensionsChanged);
    useListen(window, 'scroll', onDimensionsChanged);
    useListen(document, 'properties-view::map-resized', onDimensionsChanged);

    const onMouseEnter = (e: MouseEvent) => {
        updatePosition(e, viewportDimensions, viewportPosition);
        viewport.current?.classList.remove(VIEWPORT_MOUSE_AWAY_CLASS);
    };
    const onMouseMove = (e: MouseEvent) => {
        window.requestAnimationFrame(() => updatePosition(e, viewportDimensions, viewportPosition));
    };
    const onMouseLeave = () => {
        viewport.current?.classList.add(VIEWPORT_MOUSE_AWAY_CLASS);
    };

    const setupViewportBehavior = () => {
        const behavior = deviceWidth.isLaptopAndBigger() ? 'track-mouse' : 'scroll';
        panningImage.setInteractionBehavior(behavior);

        if (behavior === 'track-mouse') {
            viewport.current?.addEventListener('mousemove', onMouseMove);
            viewport.current?.addEventListener('mouseenter', onMouseEnter);
            viewport.current?.addEventListener('mouseleave', onMouseLeave);
        } else {
            viewport.current?.removeEventListener('mousemove', onMouseMove);
            viewport.current?.removeEventListener('mouseenter', onMouseEnter);
            viewport.current?.removeEventListener('mouseleave', onMouseLeave);
        }
    };

    onDidLoad(() => {
        refresh();
        handleView(vrStore);
        setupViewportBehavior();
        useMedia(setupViewportBehavior);
    });

    subscribe(handleView, [vrTourId]);

    function handleView(state: VirtualTourState) {
        const currentView = state[vrTourId]?.currentView;

        if (currentView?.type === '2d') {
            setView(currentView);
        }
    }

    const setView = async (view: VirtualTourImageView) => {
        if (!imageContainer.current) return;
        const { pointers, source, alt = '' } = view;

        await panningImage.setSource(source, alt);
        panningImage.resize(viewportDimensions);

        imagePointers.setPointers(pointers);
        pointerHighlighted && highlightPointers(pointerHighlighted);
    };

    const highlightPointers = (filter: VRTourPointerFilter) => {
        const highlighted = imagePointers.highlight(filter);
        if (!highlighted.length) return;

        const focus = highlighted.reduce<ScreenCoordinates>(
            (accumulatedCoordinates, currentPointer, index) => {
                const [y, x] = currentPointer.pointer.position;
                const retVal: ScreenCoordinates = {
                    x: accumulatedCoordinates.x + x,
                    y: accumulatedCoordinates.y + y,
                };
                if (index < highlighted.length - 1) return retVal;
                return {
                    x: retVal.x / highlighted.length,
                    y: retVal.y / highlighted.length,
                };
            },
            { x: 0, y: 0 },
        );
        panningImage.focusPoint(focus);
    };

    useBindMethod('highlightPointers', highlightPointers);

    return (
        <div class="vr-tour-image-view__viewport" js-hook-vr-tour-image-view-viewport>
            <div
                class="vr-tour-image-view__image-container"
                js-hook-vr-tour-image-view-image-container
            />
        </div>
    );
};

VRTourImageView.props = {
    onPointerClick: {
        type: Function,
        required: true,
    },
    pointerHighlighted: {
        type: Function,
    },
    vrTourId: {
        type: String,
        required: true,
    },
};

defineElement('vr-tour-image-view', VRTourImageView);
